import { Injectable, inject } from '@angular/core';
import { Observable, of, shareReplay, switchMap } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { FirestoreService } from '@fitup-monorepo/core/lib/services/firestore/firestore.service';
import { Store } from '@ngrx/store';
import { selectCustomerUUID } from '@fitup-monorepo/core/lib/state/profile/profile.selector';
import { DailyTasksOverview } from './model/daily-task';

@Injectable({
  providedIn: 'root',
})
export class DailyTasksService {
  private readonly firestoreService = inject(FirestoreService);
  private readonly store = inject(Store);

  /**
   * One shared firestore listener per day. The overview is reloaded on every home page refresh/resume - without
   * sharing, each of those re-ran the lookup query and re-created the firestore listener from scratch.
   */
  private readonly overviewsByDay = new Map<string, Observable<DailyTasksOverview | undefined>>();

  public getOverview(day: string): Observable<DailyTasksOverview> {
    return this.getSharedOverview(day).pipe(filter(overview => !!overview));
  }

  private getSharedOverview(day: string): Observable<DailyTasksOverview | undefined> {
    const cached = this.overviewsByDay.get(day);
    if (cached) {
      return cached;
    }

    // `selectCustomerUUID` only emits when the uuid actually changes, so a reloaded profile no longer drops the
    // listener, and a customer change re-resolves the shared stream instead of replaying somebody else's tasks
    const overview$ = this.store.select(selectCustomerUUID).pipe(
      switchMap(uuid => (uuid ? this.observeOverview(uuid, day) : of(undefined))),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.overviewsByDay.set(day, overview$);
    return overview$;
  }

  private observeOverview(uuid: string, day: string): Observable<DailyTasksOverview | undefined> {
    return this.firestoreService
      .findAndObserveDocuments<DailyTasksOverview>(`customer/${uuid}/dailyTasks`, [
        { field: 'day', operator: '==', value: day },
      ])
      .pipe(
        map(documents => documents[0]),
        switchMap(document =>
          document
            ? this.firestoreService.observeDocument<DailyTasksOverview>(`customer/${uuid}/dailyTasks/${document.id}`)
            : of(undefined)
        ),
        tap(overview => this.logOverviewChange(day, overview))
      );
  }

  private readonly lastLoggedOverviewByDay = new Map<string, string>();

  /**
   * Logs changed values only - the document is re-emitted with identical values on every refresh and resume.
   */
  private logOverviewChange(day: string, overview: DailyTasksOverview | undefined): void {
    const description = overview
      ? `tasks: ${overview.tasks?.length} progress: ${overview.progress}`
      : 'there is no daily tasks document';
    if (description !== this.lastLoggedOverviewByDay.get(day)) {
      this.lastLoggedOverviewByDay.set(day, description);
      console.log('daily-tasks: daily tasks for', day, '-', description);
    }
  }
}
